import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ChevronLeft, Info, Loader2, Plus, Target } from "lucide-react";
import { API, JURISDICTIONS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

// ============ STRATEGY PATTERN REPORT ============

const STRATEGY_CASE_TYPES = [
  { value: "employment_discrimination", label: "Employment Discrimination", desc: "FEHA, Title VII, ADA claims" },
  { value: "personal_injury", label: "Personal Injury", desc: "Auto accidents, premises liability" },
  { value: "contract_dispute", label: "Contract Dispute", desc: "Breach of contract, business disputes" },
  { value: "medical_malpractice", label: "Medical Malpractice", desc: "Healthcare provider negligence" }
];

const StrategyPatternPage = () => {
  const [form, setForm] = useState({
    case_type: "employment_discrimination",
    party_role: "plaintiff",
    jurisdiction: "california",
    include_cost_analysis: true,
    include_timeline: true,
    include_settlement_data: true
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const { trackEvent } = useEngagementTracker();
  
  // Track page view
  useEffect(() => {
    trackEvent('strategy', 'view');
  }, [trackEvent]);
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/strategy-patterns/generate`, form);
      setReport(res.data);
      // Track report generation - high-value action
      trackEvent('strategy', 'report_generated', { case_type: form.case_type, party_role: form.party_role });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  const formatPercent = (n) => `${(n * 100).toFixed(0)}%`;
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="strategy-pattern-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Strategy Pattern Report</h1>
            <Badge className="bg-cyan-500">NEW</Badge>
          </div>
          <p className="text-muted-foreground">Historical litigation patterns, benchmarks, and strategy intelligence</p>
        </div>
        
        <Alert className="mb-6 bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            This report presents historical patterns derived from aggregated public court records. 
            It does not constitute legal advice or recommend specific strategies. All data reflects 
            historical trends and may not predict future outcomes.
          </AlertDescription>
        </Alert>
        
        {!report ? (
          <Card>
            <CardHeader>
              <CardTitle>Generate Strategy Pattern Report</CardTitle>
              <CardDescription>Select case parameters to generate a comprehensive historical analysis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base font-semibold mb-3 block">Case Type</Label>
                <div className="grid gap-3 md:grid-cols-2">
                  {STRATEGY_CASE_TYPES.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setForm(p => ({ ...p, case_type: type.value }))}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        form.case_type === type.value 
                          ? 'border-cyan-500 bg-cyan-50' 
                          : 'hover:border-slate-300'
                      }`}
                    >
                      <h4 className="font-medium">{type.label}</h4>
                      <p className="text-xs text-muted-foreground">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Party Role</Label>
                  <Select value={form.party_role} onValueChange={v => setForm(p => ({ ...p, party_role: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="plaintiff">Plaintiff / Claimant</SelectItem>
                      <SelectItem value="defense">Defense / Respondent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Jurisdiction</Label>
                  <Select value={form.jurisdiction} onValueChange={v => setForm(p => ({ ...p, jurisdiction: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-3">
                <Label className="text-base font-semibold">Include in Report</Label>
                <div className="flex flex-wrap gap-4">
                  {[
                    ["include_timeline", "Timeline Benchmarks"],
                    ["include_cost_analysis", "Cost Analysis"],
                    ["include_settlement_data", "Settlement Patterns"]
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form[key]}
                        onChange={e => setForm(p => ({ ...p, [key]: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <Button className="w-full" size="lg" onClick={generate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Target className="h-4 w-4 mr-2" />}
                Generate Strategy Pattern Report
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{report.case_type}</h2>
                <p className="text-muted-foreground">{report.party_role} Strategy Patterns • {report.jurisdiction}</p>
              </div>
              <Button variant="outline" onClick={() => setReport(null)}>
                <Plus className="h-4 w-4 mr-2" /> New Report
              </Button>
            </div>
            
            {/* Case Profile */}
            <Card>
              <CardHeader>
                <CardTitle>Historical Case Profile</CardTitle>
                <CardDescription>Based on aggregated public court records</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-muted-foreground mb-1">Avg Duration</p>
                    <p className="text-2xl font-bold">{report.case_profile?.historical_metrics?.avg_duration_months} mo</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-xl text-center">
                    <p className="text-sm text-emerald-700 mb-1">Settlement Rate</p>
                    <p className="text-2xl font-bold text-emerald-700">{formatPercent(report.case_profile?.historical_metrics?.settlement_rate)}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-xl text-center">
                    <p className="text-sm text-blue-700 mb-1">Trial Rate</p>
                    <p className="text-2xl font-bold text-blue-700">{formatPercent(report.case_profile?.historical_metrics?.trial_rate)}</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-xl text-center">
                    <p className="text-sm text-amber-700 mb-1">Dismissal Rate</p>
                    <p className="text-2xl font-bold text-amber-700">{formatPercent(report.case_profile?.historical_metrics?.dismissal_rate)}</p>
                  </div>
                </div>
                
                {report.case_profile?.outcome_distribution && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Outcome Distribution</h4>
                    <div className="space-y-2">
                      {Object.entries(report.case_profile.outcome_distribution).map(([outcome, rate]) => (
                        <div key={outcome} className="flex items-center gap-3">
                          <span className="text-sm w-48 capitalize">{outcome.replace(/_/g, ' ')}</span>
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${rate * 100}%` }} />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{formatPercent(rate)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Strategy Patterns */}
            {report.strategy_patterns && (
              <Card>
                <CardHeader>
                  <CardTitle>{form.party_role === 'plaintiff' ? 'Plaintiff' : 'Defense'} Strategy Patterns Observed</CardTitle>
                  <CardDescription>Historical patterns employed in similar cases</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="early_phase">
                    <TabsList className="w-full justify-start mb-4">
                      <TabsTrigger value="early_phase">Early Phase</TabsTrigger>
                      <TabsTrigger value="discovery_phase">Discovery</TabsTrigger>
                      <TabsTrigger value={form.party_role === 'plaintiff' ? 'motion_practice' : 'dispositive_motions'}>
                        {form.party_role === 'plaintiff' ? 'Motion Practice' : 'Dispositive Motions'}
                      </TabsTrigger>
                    </TabsList>
                    
                    {Object.entries(report.strategy_patterns).map(([phase, patterns]) => (
                      <TabsContent key={phase} value={phase} className="space-y-3">
                        {patterns.map((p, i) => (
                          <div key={i} className="p-4 border rounded-xl">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium">{p.pattern}</h4>
                              <Badge variant="outline">{formatPercent(p.usage_rate)} usage</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{p.impact}</p>
                          </div>
                        ))}
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            )}
            
            {/* Opposing Patterns Summary */}
            {report.opposing_patterns_summary && (
              <Card>
                <CardHeader>
                  <CardTitle>Opposing Side Patterns to Anticipate</CardTitle>
                  <CardDescription>{report.opposing_patterns_summary.note}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {report.opposing_patterns_summary.key_patterns.map((pattern, i) => (
                      <Badge key={i} variant="outline" className="py-1.5">{pattern}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Timeline Benchmarks */}
            {report.timeline_benchmarks && (
              <Card>
                <CardHeader>
                  <CardTitle>Timeline Benchmarks</CardTitle>
                  <CardDescription>Historical duration patterns for similar cases</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {Object.entries(report.timeline_benchmarks).map(([phase, data]) => (
                      <div key={phase} className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-sm text-muted-foreground mb-1 capitalize">{phase.replace(/_/g, ' ')}</p>
                        <p className="text-xl font-bold">
                          {data.avg_days ? `${data.avg_days} days` : data.avg_months ? `${data.avg_months} mo` : 'N/A'}
                        </p>
                        <p className="text-xs text-muted-foreground">Range: {data.range}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Cost Analysis */}
            {report.cost_benchmarks && (
              <Card>
                <CardHeader>
                  <CardTitle>Cost Benchmarks</CardTitle>
                  <CardDescription>Historical litigation cost ranges</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 md:grid-cols-2">
                    {Object.entries(report.cost_benchmarks).map(([party, costs]) => (
                      <div key={party}>
                        <h4 className="font-semibold mb-3 capitalize">{party} Costs</h4>
                        <div className="space-y-2">
                          {Object.entries(costs).map(([phase, range]) => (
                            <div key={phase} className="flex justify-between p-3 bg-slate-50 rounded-lg">
                              <span className="text-sm capitalize">{phase.replace(/_/g, ' ')}</span>
                              <span className="font-medium">{formatCurrency(range.low)} - {formatCurrency(range.high)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Settlement Patterns */}
            {report.settlement_patterns && (
              <Card>
                <CardHeader>
                  <CardTitle>Settlement Value Patterns</CardTitle>
                  <CardDescription>Historical settlement ranges by stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    {Object.entries(report.settlement_patterns.ranges).map(([stage, range]) => (
                      <div key={stage} className="p-4 border rounded-xl">
                        <p className="text-sm text-muted-foreground mb-1 capitalize">{stage.replace(/_/g, ' ')}</p>
                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(range.median)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(range.low)} - {formatCurrency(range.high)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {report.settlement_patterns.factors_correlating_higher && (
                    <div className="mt-6">
                      <h4 className="font-semibold mb-3">Factors Historically Correlating with Higher Settlements</h4>
                      <div className="flex flex-wrap gap-2">
                        {report.settlement_patterns.factors_correlating_higher.map((factor, i) => (
                          <Badge key={i} className="bg-emerald-100 text-emerald-700">{factor}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Disclaimer */}
            <Alert className="bg-slate-100 border-slate-300">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {report.disclaimer}
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default StrategyPatternPage;
